<template>
  <v-container fluid>
    <section>
      <KpiCards class="mb-3" :items="kpiItems" :loading="loading && !menus.length" :skeleton="4" />

      <v-card rounded="lg" outlined elevation="0" class="ps-datatable-card mb-3">
        <v-card-text>
          <div class="row mb-2">
            <div class="col-12 col-md-6">
              <v-card-title class="px-0 pt-0 ps-products-title">
                {{ $t("menu_management") }}
              </v-card-title>
            </div>
            <div class="col-12 col-md-6 text-right">
              <v-btn
                depressed
                outlined
                color="primary"
                class="mr-2 rounded-lg"
                @click="newHeader"
              >
                <v-icon left small>fal fa-layer-group</v-icon>
                {{ $t("menu_header") }}
              </v-btn>
              <v-btn
                depressed
                color="primary"
                class="white--text btn-primary-brand"
                @click="newItem(null)"
              >
                <v-icon left small>fal fa-plus-circle</v-icon>
                {{ $t("new_data") }}
              </v-btn>
            </div>
          </div>

          <v-row dense class="mb-2 align-end">
            <v-col cols="12" sm="6" md="3">
              <TextSearch v-model="filters.q" />
            </v-col>
            <v-col cols="6" sm="3" md="2">
              <TextSelect
                :dense="true"
                label="type"
                :items="typeFilterItems"
                text="text"
                itemValue="value"
                :returnObject="false"
                :searchable="false"
                v-model="filters.type"
              />
            </v-col>
            <v-col cols="6" sm="3" md="2">
              <TextSelect
                :dense="true"
                label="menu_type"
                :items="menuTypeFilterItems"
                text="text"
                itemValue="value"
                :returnObject="false"
                v-model="filters.menu_type"
              />
            </v-col>
            <v-col cols="6" sm="3" md="2">
              <TextSelect
                :dense="true"
                label="menu_header"
                :items="groupFilterItems"
                text="text"
                itemValue="value"
                :returnObject="false"
                v-model="filters.group"
              />
            </v-col>
            <v-col cols="6" sm="3" md="2">
              <TextSelect
                :dense="true"
                label="status"
                :items="statusFilterItems"
                text="text"
                itemValue="value"
                :returnObject="false"
                :searchable="false"
                v-model="filters.status"
              />
            </v-col>
            <v-col cols="12" md="1" class="pb-2">
              <v-btn v-if="isFiltered" text small color="primary" @click="clearFilters">
                <v-icon left x-small>fal fa-times</v-icon>
                {{ $t("clear") }}
              </v-btn>
            </v-col>
          </v-row>

          <v-data-table
            :headers="menuTableHeaders"
            :items="menuRows"
            item-key="key"
            :loading="loading"
            :items-per-page="30"
            :footer-props="{ 'items-per-page-options': rowsPerPageItems }"
            :no-data-text="$t('no_data')"
          >
            <template v-slot:[`item.name`]="{ item: row }">
              <div class="d-flex align-center" :style="{ paddingLeft: row.level * 28 + 'px' }">
                <v-avatar
                  color="grey lighten-4"
                  :size="row.level ? 26 : 32"
                  class="rounded-lg mr-3"
                >
                  <app-img
                    v-if="isUploadedIcon(row.item) && row.item.icon"
                    :img="row.item.icon"
                    width="20"
                    height="20"
                    contain
                  />
                  <i
                    v-else-if="isLineIcon(row.item) && row.item.icon"
                    :class="row.item.icon"
                    class="ps-menu-lni"
                  ></i>
                  <v-icon v-else x-small :color="row.level ? 'grey' : 'primary'">{{
                    iconType(row.item) === "font_awesome" && row.item.icon
                      ? row.item.icon
                      : row.level
                      ? "fal fa-circle"
                      : "fal fa-folder"
                  }}</v-icon>
                </v-avatar>
                <div>
                  <div :class="row.level ? 'body-2' : 'font-weight-bold'">
                    {{ displayName(row.item) }}
                  </div>
                  <div class="caption grey--text">{{ row.item.code }}</div>
                </div>
              </div>
            </template>
            <template v-slot:[`item.to`]="{ item: row }">
              {{ row.item.to || "-" }}
            </template>
            <template v-slot:[`item.group`]="{ item: row }">
              <v-chip
                v-if="resolveMenuHeader(row.item)"
                x-small
                label
                outlined
                color="secondary"
              >
                {{ displayName(resolveMenuHeader(row.item)) }}
              </v-chip>
              <span v-else class="caption grey--text">{{ $t("menu_no_header") }}</span>
            </template>
            <template v-slot:[`item.menu_type`]="{ item: row }">
              <v-chip x-small label outlined>{{ row.item.menu_type }}</v-chip>
            </template>
            <template v-slot:[`item.type`]="{ item: row }">
              <v-chip
                x-small
                label
                dark
                :color="row.item.type === 'admin' ? 'deep-purple' : 'primary'"
              >
                {{ row.item.type }}
              </v-chip>
            </template>
            <template v-slot:[`item.is_active`]="{ item: row }">
              <v-switch
                :input-value="!!row.item.is_active"
                dense
                hide-details
                inset
                class="mt-0 pt-0"
                role="switch"
                :aria-label="activeSwitchLabel(row.item)"
                :aria-checked="String(!!row.item.is_active)"
                @change="toggleItem(row.item)"
              />
            </template>
            <template v-slot:[`item.actions`]="{ item: row }">
              <v-btn
                icon
                x-small
                :disabled="!canMove(row, -1)"
                :title="$t('menu_move_up')"
                @click="moveItem(row, -1)"
              >
                <v-icon x-small>fal fa-arrow-up</v-icon>
              </v-btn>
              <v-btn
                icon
                x-small
                :disabled="!canMove(row, 1)"
                :title="$t('menu_move_down')"
                @click="moveItem(row, 1)"
              >
                <v-icon x-small>fal fa-arrow-down</v-icon>
              </v-btn>
              <v-btn
                v-if="!row.level"
                fab
                x-small
                depressed
                class="rounded-lg ml-1"
                :title="$t('add')"
                @click="newItem(row.item.id)"
              >
                <v-icon x-small>fal fa-plus</v-icon>
              </v-btn>
              <v-btn
                fab
                x-small
                depressed
                class="rounded-lg ml-1"
                @click="editItem(row.item)"
              >
                <v-icon x-small>fal fa-edit</v-icon>
              </v-btn>
              <v-btn
                fab
                x-small
                depressed
                class="rounded-lg ml-1"
                color="red lighten-5"
                :disabled="hasChildMenus(row.item)"
                :title="hasChildMenus(row.item) ? $t('delete') + ' disabled' : $t('delete')"
                @click="deleteItem(row.item)"
              >
                <v-icon x-small color="red">fal fa-trash</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <v-card rounded="lg" outlined elevation="0" class="ps-datatable-card">
        <v-card-text>
          <v-card-title class="px-0 pt-0 ps-products-title">
            {{ $t("menu_headers") }}
          </v-card-title>
          <v-data-table
            :headers="headerTableHeaders"
            :items="filteredHeaders"
            :items-per-page="30"
            :footer-props="{ 'items-per-page-options': rowsPerPageItems }"
            :no-data-text="$t('no_data')"
          >
            <template v-slot:[`item.name`]="{ item }">
              <div class="font-weight-medium">{{ displayName(item) }}</div>
              <div class="caption grey--text">{{ item.code }}</div>
            </template>
            <template v-slot:[`item.type`]="{ item }">
              <v-chip
                x-small
                label
                dark
                :color="item.type === 'admin' ? 'deep-purple' : 'primary'"
              >
                {{ item.type }}
              </v-chip>
            </template>
            <template v-slot:[`item.menu_type`]="{ item }">
              <v-chip x-small label outlined>{{ item.menu_type }}</v-chip>
            </template>
            <template v-slot:[`item.is_active`]="{ item }">
              <v-chip x-small label dark :color="item.is_active ? 'green' : 'red'">
                {{ item.is_active ? $t("active") : $t("inactive") }}
              </v-chip>
            </template>
            <template v-slot:[`item.menus`]="{ item }">
              <template v-if="headerMenus(item).length">
                <v-chip
                  v-for="menu in headerMenus(item)"
                  :key="menu.id"
                  x-small
                  outlined
                  color="secondary"
                  class="mr-1 mb-1"
                >
                  {{ displayName(menu) }}
                </v-chip>
              </template>
              <span v-else class="caption grey--text">{{ $t("no_data") }}</span>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-btn
                icon
                x-small
                :disabled="!canMoveHeader(item, -1)"
                :title="$t('menu_move_up')"
                @click="moveHeader(item, -1)"
              >
                <v-icon x-small>fal fa-arrow-up</v-icon>
              </v-btn>
              <v-btn
                icon
                x-small
                :disabled="!canMoveHeader(item, 1)"
                :title="$t('menu_move_down')"
                @click="moveHeader(item, 1)"
              >
                <v-icon x-small>fal fa-arrow-down</v-icon>
              </v-btn>
              <v-btn
                fab
                x-small
                depressed
                class="rounded-lg ml-1"
                @click="editHeader(item)"
              >
                <v-icon x-small>fal fa-edit</v-icon>
              </v-btn>
              <v-btn
                fab
                x-small
                depressed
                class="rounded-lg ml-1"
                color="red lighten-5"
                :disabled="headerMenus(item).length > 0"
                @click="deleteHeader(item)"
              >
                <v-icon x-small color="red">fal fa-trash</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </section>

    <!-- add / edit dialog -->
    <app-custom-dialog
      v-model="dialog"
      :title="editing ? 'edit' : 'new_data'"
      :submit-text="'save'"
      @submit="submitForm"
    >
      <div slot="activator">
        <v-form ref="form" v-model="valid">
          <v-row dense>
            <!-- icon shown above the other fields -->
            <v-col cols="12" sm="6">
              <label class="caption">{{ $t("icon_type") }}</label>
              <v-select
                v-model="form.icon_type"
                :items="iconTypeOptions"
                item-text="label"
                item-value="value"
                dense
                outlined
                @change="onIconTypeChange"
              />
            </v-col>
            <v-col v-if="form.icon_type !== 'none'" cols="12" sm="6">
              <label class="caption">{{ $t("icon") }}</label>
              <!-- image / svg: upload a file, stored as a URL like categories -->
              <DragDropUpload
                v-if="form.icon_type === 'image' || form.icon_type === 'svg'"
                v-model="iconFile"
                accept=".svg,image/svg+xml,image/png,image/jpeg"
                :max-size="2 * 1024 * 1024"
                :max-size-m-b="2"
              />
              <!-- font awesome class -->
              <v-text-field
                v-else
                v-model="form.icon"
                dense
                outlined
                placeholder="fal fa-bars"
              >
                <template #prepend-inner>
                  <v-icon small>{{ form.icon || "fal fa-bars" }}</v-icon>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <label class="caption">{{ $t("code") }} *</label>
              <v-text-field
                v-model="form.code"
                dense
                outlined
                :rules="[(v) => !!v || $t('required')]"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <label class="caption">{{ $t("name_la") }} *</label>
              <v-text-field
                v-model="form.name_la"
                dense
                outlined
                :rules="[(v) => !!v || $t('required')]"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <label class="caption">{{ $t("name_en") }} *</label>
              <v-text-field
                v-model="form.name_en"
                dense
                outlined
                :rules="[(v) => !!v || $t('required')]"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <label class="caption">{{ $t("name_th") }}</label>
              <v-text-field v-model="form.name_th" dense outlined />
            </v-col>
            <v-col cols="12" sm="6">
              <label class="caption">{{ $t("name_cn") }}</label>
              <v-text-field v-model="form.name_cn" dense outlined />
            </v-col>
            <v-col cols="12" sm="6">
              <label class="caption">{{ $t("link") }}</label>
              <v-text-field
                v-model="form.to"
                dense
                outlined
                placeholder="/example"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <label class="caption">{{ $t("parent_menu") }}</label>
              <v-select
                v-model="form.parent_id"
                :items="parentOptions"
                item-text="label"
                item-value="value"
                dense
                outlined
                clearable
              />
            </v-col>
            <v-col cols="12" sm="6">
              <label class="caption">{{ $t("menu_header") }}</label>
              <v-select
                v-model="form.menu_header_id"
                :items="menuHeaderOptions"
                item-text="label"
                item-value="value"
                dense
                outlined
                clearable
              />
            </v-col>
            <v-col cols="12" sm="6">
              <label class="caption">{{ $t("menu_type") }}</label>
              <v-select
                v-model="form.menu_type"
                :items="menuTypes"
                dense
                outlined
              />
            </v-col>
            <v-col cols="12" sm="6">
              <label class="caption">{{ $t("type") }}</label>
              <v-select
                v-model="form.type"
                :items="['user', 'admin']"
                dense
                outlined
              />
            </v-col>
            <v-col cols="12" sm="6">
              <label class="caption">{{ $t("position") }}</label>
              <v-text-field
                v-model.number="form.position"
                type="number"
                dense
                outlined
              />
            </v-col>
            <v-col cols="12" sm="6" class="d-flex align-center">
              <v-switch
                v-model="form.is_active"
                :label="$t('active')"
                dense
                hide-details
                inset
                role="switch"
                tabindex="0"
                :aria-label="$t('active')"
                :aria-checked="String(!!form.is_active)"
              />
            </v-col>
          </v-row>
        </v-form>
      </div>
    </app-custom-dialog>

    <app-custom-dialog
      v-model="headerDialog"
      :title="headerEditing ? 'edit' : 'new_data'"
      :submit-text="'save'"
      @submit="submitHeaderForm"
    >
      <div slot="activator">
        <v-form ref="headerForm" v-model="headerValid">
          <v-row dense>
            <v-col cols="12" sm="6">
              <label class="caption">{{ $t("code") }} *</label>
              <v-text-field
                v-model="headerForm.code"
                dense
                outlined
                :rules="[(v) => !!v || $t('required')]"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <label class="caption">{{ $t("name_la") }} *</label>
              <v-text-field
                v-model="headerForm.name_la"
                dense
                outlined
                :rules="[(v) => !!v || $t('required')]"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <label class="caption">{{ $t("name_en") }} *</label>
              <v-text-field
                v-model="headerForm.name_en"
                dense
                outlined
                :rules="[(v) => !!v || $t('required')]"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <label class="caption">{{ $t("name_th") }}</label>
              <v-text-field v-model="headerForm.name_th" dense outlined />
            </v-col>
            <v-col cols="12" sm="6">
              <label class="caption">{{ $t("name_cn") }}</label>
              <v-text-field v-model="headerForm.name_cn" dense outlined />
            </v-col>
            <v-col cols="12" sm="6">
              <label class="caption">{{ $t("type") }} *</label>
              <v-select
                v-model="headerForm.type"
                :items="['user', 'admin']"
                dense
                outlined
                :rules="[(v) => !!v || $t('required')]"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <label class="caption">{{ $t("menu_type") }} *</label>
              <v-select
                v-model="headerForm.menu_type"
                :items="menuTypes"
                dense
                outlined
                :rules="[(v) => !!v || $t('required')]"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <label class="caption">{{ $t("position") }}</label>
              <v-text-field
                v-model.number="headerForm.position"
                type="number"
                dense
                outlined
              />
            </v-col>
            <v-col cols="12" sm="6" class="d-flex align-center">
              <v-switch
                v-model="headerForm.is_active"
                :label="$t('active')"
                dense
                hide-details
                inset
                role="switch"
                tabindex="0"
                :aria-label="$t('active')"
                :aria-checked="String(!!headerForm.is_active)"
              />
            </v-col>
          </v-row>
        </v-form>
      </div>
    </app-custom-dialog>
  </v-container>
</template>

<script>
import DragDropUpload from "@/components/global/DragDropUpload.vue";
import TextSelect from "@/components/global/TextSelect";
import Help from "@/utils/helper";
import { myMixin } from "@/mixin/main";
import { success, toast, error, confirmAlert } from "@/controllers/alert";

const emptyForm = () => ({
  id: null,
  code: "",
  name_la: "",
  name_en: "",
  name_th: "",
  name_cn: "",
  icon: "",
  icon_type: "font_awesome",
  to: "",
  parent_id: null,
  menu_header_id: null,
  position: null,
  type: "user",
  menu_type: "ALL",
  is_active: true,
});

const emptyHeaderForm = () => ({
  id: null,
  code: "",
  name_la: "",
  name_en: "",
  name_th: "",
  name_cn: "",
  position: null,
  type: "user",
  menu_type: "ALL",
  is_active: true,
});

export default {
  name: "MenuManagement",
  components: { DragDropUpload, TextSelect },
  mixins: [myMixin],

  data: () => ({
    loading: false,
    iconFile: null,
    dialog: false,
    headerDialog: false,
    editing: false,
    headerEditing: false,
    valid: true,
    headerValid: true,
    menus: [],
    menuHeaders: [],
    filters: { q: "", type: null, menu_type: null, group: null, status: null },
    form: emptyForm(),
    headerForm: emptyHeaderForm(),
    menuTypes: [
      "ALL",
      "POS",
      "RESTAURANT",
      "BUFFET",
      "HOTEL",
      "PHARMACY",
      "COFFEE",
      "BAR",
      "KARAOKE",
      "SPA",
      "SALON",
      "GYM",
      "RETAIL",
      "WHOLESALE",
      "ONLINE_SHOP",
      "OTHERS",
    ],
    iconTypeOptions: [
      { value: "font_awesome", label: "Font Awesome" },
      { value: "line", label: "Line Icons" },
      { value: "image", label: "Image" },
      { value: "svg", label: "SVG" },
      { value: "none", label: "None" },
    ],
  }),

  created() {
    this.fetchMenus();
    this.fetchMenuHeaders();
  },

  computed: {
    rowsPerPageItems() {
      return this.$store.getters["index/rowsPerPageItems"] || [15, 30, 50];
    },
    isFiltered() {
      const f = this.filters;
      return !!(f.q || f.type || f.menu_type || f.group !== null || f.status);
    },
    typeFilterItems() {
      return [
        { value: null, text: this.$t("all") },
        { value: "user", text: "user" },
        { value: "admin", text: "admin" },
      ];
    },
    menuTypeFilterItems() {
      return [
        { value: null, text: this.$t("all") },
        ...this.menuTypes.map((v) => ({ value: v, text: v })),
      ];
    },
    // group = menu header; 0 means menus without a header
    groupFilterItems() {
      return [
        { value: null, text: this.$t("all") },
        { value: 0, text: this.$t("menu_no_header") },
        ...this.menuHeaders.map((h) => ({ value: h.id, text: this.displayName(h) })),
      ];
    },
    statusFilterItems() {
      return [
        { value: null, text: this.$t("all") },
        { value: "active", text: this.$t("active") },
        { value: "inactive", text: this.$t("inactive") },
      ];
    },
    allMenuItems() {
      return this.menus.flatMap((m) => [m, ...(m.all_menu || [])]);
    },
    kpiItems() {
      const all = this.allMenuItems;
      const active = all.filter((m) => !!m.is_active).length;
      return [
        { key: "total", label: this.$t("menu_total_menus"), icon: "fal fa-bars", value: all.length },
        { key: "active", label: this.$t("active"), icon: "fal fa-check-circle", value: active },
        { key: "inactive", label: this.$t("inactive"), icon: "fal fa-pause-circle", value: all.length - active },
        { key: "groups", label: this.$t("menu_headers"), icon: "fal fa-layer-group", value: this.menuHeaders.length },
      ];
    },
    menuTableHeaders() {
      return [
        { text: this.$t("name"), value: "name", sortable: false },
        { text: this.$t("menu_path"), value: "to", sortable: false },
        { text: this.$t("menu_header"), value: "group", sortable: false },
        { text: this.$t("menu_type"), value: "menu_type", sortable: false },
        { text: this.$t("type"), value: "type", sortable: false },
        { text: this.$t("active"), value: "is_active", sortable: false },
        { text: this.$t("manage"), value: "actions", sortable: false, align: "right" },
      ];
    },
    headerTableHeaders() {
      return [
        { text: this.$t("name"), value: "name", sortable: false },
        { text: this.$t("type"), value: "type", sortable: false },
        { text: this.$t("menu_type"), value: "menu_type", sortable: false },
        { text: this.$t("position"), value: "position", sortable: false },
        { text: this.$t("status"), value: "is_active", sortable: false },
        { text: this.$t("menus"), value: "menus", sortable: false },
        { text: this.$t("manage"), value: "actions", sortable: false, align: "right" },
      ];
    },
    filteredHeaders() {
      const { type, menu_type, group, status, q } = this.filters;
      const needle = (q || "").trim().toLowerCase();
      return this.menuHeaders.filter((h) => {
        if (type && h.type !== type) return false;
        if (menu_type && h.menu_type !== menu_type) return false;
        if (group !== null && (group === 0 || String(h.id) !== String(group))) return false;
        if (status && !!h.is_active !== (status === "active")) return false;
        if (needle) {
          const hay = [h.code, h.name_la, h.name_en, h.name_th, h.name_cn]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          if (!hay.includes(needle)) return false;
        }
        return true;
      });
    },
    // Flat table rows: a top-level menu stays when it or one of its children matches;
    // its children are narrowed to the matching ones. Rows wrap the original objects.
    menuRows() {
      const rows = [];
      this.menus.forEach((parent) => {
        const children = (parent.all_menu || []).filter(this.matchesFilters);
        if (!this.matchesFilters(parent) && !children.length) return;
        rows.push({ key: `m${parent.id}`, item: parent, level: 0, parent: null });
        children.forEach((child) =>
          rows.push({ key: `m${child.id}`, item: child, level: 1, parent })
        );
      });
      return rows;
    },
    // top-level menus are valid parents (a menu cannot be its own parent)
    parentOptions() {
      return this.menus
        .filter((m) => m.id !== this.form.id)
        .map((m) => ({ label: this.displayName(m), value: m.id }));
    },
    menuHeaderOptions() {
      return this.menuHeaders.map((m) => ({
        label: this.displayName(m),
        value: m.id,
      }));
    },
  },

  methods: {
    matchesFilters(item) {
      const { type, menu_type, group, status, q } = this.filters;
      if (type && item.type !== type) return false;
      if (menu_type && item.menu_type !== menu_type) return false;
      if (status && !!item.is_active !== (status === "active")) return false;
      if (group !== null) {
        const header = this.resolveMenuHeader(item);
        if (group === 0 ? !!header : String(header?.id) !== String(group)) return false;
      }
      const needle = (q || "").trim().toLowerCase();
      if (needle) {
        const hay = [item.code, item.to, item.name_la, item.name_en, item.name_th, item.name_cn]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    },
    // reorder among siblings in the full (unfiltered) lists, so it works while filtering
    siblingsOf(row) {
      return row.level ? row.parent.all_menu : this.menus;
    },
    canMove(row, dir) {
      const list = this.siblingsOf(row);
      const to = list.indexOf(row.item) + dir;
      return to >= 0 && to < list.length;
    },
    moveItem(row, dir) {
      if (!this.canMove(row, dir)) return;
      const list = this.siblingsOf(row);
      const from = list.indexOf(row.item);
      list.splice(from, 1);
      list.splice(from + dir, 0, row.item);
      if (row.level) this.onReorderChild(row.parent);
      else this.onReorderTop();
    },
    canMoveHeader(header, dir) {
      const to = this.menuHeaders.findIndex((h) => h.id === header.id) + dir;
      return to >= 0 && to < this.menuHeaders.length;
    },
    moveHeader(header, dir) {
      if (!this.canMoveHeader(header, dir)) return;
      const from = this.menuHeaders.findIndex((h) => h.id === header.id);
      const [moved] = this.menuHeaders.splice(from, 1);
      this.menuHeaders.splice(from + dir, 0, moved);
      this.onReorderHeaders();
    },
    clearFilters() {
      this.filters = { q: "", type: null, menu_type: null, group: null, status: null };
    },
    displayName(item) {
      if (!item) return "";
      const byLocale = {
        la: item.name_la,
        en: item.name_en,
        th: item.name_th,
        ch: item.name_cn,
      };
      return byLocale[this.$i18n.locale] || item.name_en || item.name_la;
    },
    activeSwitchLabel(item) {
      const name = this.displayName(item);
      return `${this.$t("active")} ${name || ""}`.trim();
    },
    hasChildMenus(item) {
      if (!item?.id) return false;
      if (item.all_menu && item.all_menu.length) return true;

      const hasChild = (items = []) =>
        items.some((menu) => {
          if (String(menu.parent_id) === String(item.id)) {
            return true;
          }
          return hasChild(menu.all_menu || []) || hasChild(menu.menu || []);
        });

      return hasChild(this.menus);
    },

    async fetchMenus() {
      this.loading = true;
      try {
        const { data } = await this.$http.get("menu_manage");
        if (data.status) {
          this.menus = data.data || [];
          this.mergeMenuHeadersFromMenus(this.menus);
        }
      } catch (e) {
        error(this, this.errMsg(e));
      } finally {
        this.loading = false;
      }
    },

    async fetchMenuHeaders() {
      try {
        const { data } = await this.$http.get("menu-headers");
        const headers = this.normalizeListResponse(data);
        if (headers.length || !this.menuHeaders.length) {
          this.menuHeaders = headers;
        }
      } catch (e) {
        error(this, this.errMsg(e));
      }
    },

    normalizeListResponse(data) {
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.data)) return data.data;
      if (Array.isArray(data?.data?.data)) return data.data.data;
      return [];
    },

    mergeMenuHeadersFromMenus(menus) {
      const existing = {};
      this.menuHeaders.forEach((header) => {
        if (header?.id) existing[header.id] = header;
      });

      const collect = (items = []) => {
        items.forEach((item) => {
          if (item?.menu_header?.id) {
            existing[item.menu_header.id] = item.menu_header;
          }
          if (item?.all_menu?.length) collect(item.all_menu);
          if (item?.menu?.length) collect(item.menu);
        });
      };

      collect(menus);
      this.menuHeaders = Object.values(existing).sort(
        (a, b) => (a.position || 0) - (b.position || 0)
      );
    },

    resolveMenuHeader(item) {
      if (!item) return null;
      if (item.menu_header) return item.menu_header;
      if (!item.menu_header_id) return null;
      return (
        this.menuHeaders.find(
          (header) => String(header.id) === String(item.menu_header_id)
        ) || null
      );
    },

    headerMenus(header) {
      if (!header?.id) return [];
      const results = [];

      const collect = (items = []) => {
        items.forEach((item) => {
          const itemHeader = this.resolveMenuHeader(item);
          if (String(itemHeader?.id) === String(header.id)) {
            results.push(item);
          }
          if (item?.all_menu?.length) collect(item.all_menu);
          if (item?.menu?.length) collect(item.menu);
        });
      };

      collect(this.menus);
      return results;
    },

    newItem(parentId) {
      this.editing = false;
      this.form = emptyForm();
      this.form.parent_id = parentId;
      this.iconFile = null;
      if (this.$refs.form) this.$refs.form.resetValidation();
      this.dialog = true;
    },

    editItem(item) {
      this.editing = true;
      const type = this.iconType(item);
      this.form = {
        id: item.id,
        code: item.code,
        name_la: item.name_la,
        name_en: item.name_en,
        name_th: item.name_th || "",
        name_cn: item.name_cn || "",
        icon: type === "font_awesome" ? item.icon || "" : "",
        icon_type: type,
        to: item.to || "",
        parent_id: item.parent_id,
        menu_header_id: item.menu_header_id,
        position: item.position,
        type: item.type || "user",
        menu_type: item.menu_type || "ALL",
        is_active: !!item.is_active,
      };
      // preview the existing uploaded icon (a URL string) in the upload box
      this.iconFile =
        (type === "image" || type === "svg") && item.icon
          ? Help.img(item.icon)
          : null;
      this.dialog = true;
    },

    // resolve how an item's icon should be rendered (falls back for legacy rows)
    iconType(item) {
      return item.icon_type || (item.icon ? "font_awesome" : "none");
    },

    isUploadedIcon(item) {
      const t = this.iconType(item);
      return t === "image" || t === "svg";
    },

    isLineIcon(item) {
      const icon = item && item.icon;
      return (
        typeof icon === "string" &&
        (this.iconType(item) === "line" || /^lni\s/i.test(icon))
      );
    },

    onIconTypeChange(val) {
      // keep the row consistent when switching how the icon is provided
      if (val === "font_awesome" || val === "none") this.iconFile = null;
      if (val === "image" || val === "svg") this.form.icon = "";
      if (val === "none") this.form.icon = "";
    },

    async submitForm() {
      if (this.$refs.form && !this.$refs.form.validate()) return;
      const usesFile =
        this.form.icon_type === "image" || this.form.icon_type === "svg";
      const fields = {
        code: this.form.code,
        name_la: this.form.name_la,
        name_en: this.form.name_en,
        name_th: this.form.name_th,
        name_cn: this.form.name_cn,
        to: this.form.to,
        parent_id: this.form.parent_id,
        menu_header_id: this.form.menu_header_id,
        position: this.form.position,
        type: this.form.type,
        menu_type: this.form.menu_type,
        icon_type: this.form.icon_type,
        is_active: this.form.is_active ? 1 : 0,
      };
      if (!usesFile) {
        fields.icon = this.form.icon_type === "none" ? null : this.form.icon;
      }

      // when a new file is picked, send multipart; otherwise plain JSON
      const hasNewFile = usesFile && this.iconFile instanceof File;
      let body = fields;
      let config;
      if (hasNewFile) {
        const fd = new FormData();
        Object.keys(fields).forEach((k) => {
          if (fields[k] !== undefined && fields[k] !== null)
            fd.append(k, fields[k]);
        });
        fd.append("icon_file", this.iconFile);
        body = fd;
        config = { headers: { "Content-Type": "multipart/form-data" } };
      }

      try {
        let res;
        if (this.editing) {
          // PHP can't parse multipart PUT bodies; POST with _method=PUT spoofing
          if (body instanceof FormData) {
            body.append("_method", "PUT");
            res = await this.$http.post(
              `update_menu/${this.form.id}`,
              body,
              config
            );
          } else {
            res = await this.$http.put(`update_menu/${this.form.id}`, body);
          }
        } else {
          res = await this.$http.post("add_menu", body, config);
        }
        if (res.data.status) {
          this.dialog = false;
          this.iconFile = null;
          await success(this, res.data.message);
          await this.fetchMenus();
        }
      } catch (e) {
        error(this, this.errMsg(e));
      }
    },

    newHeader() {
      this.headerEditing = false;
      this.headerForm = emptyHeaderForm();
      if (this.$refs.headerForm) this.$refs.headerForm.resetValidation();
      this.headerDialog = true;
    },

    editHeader(item) {
      this.headerEditing = true;
      this.headerForm = {
        id: item.id,
        code: item.code,
        name_la: item.name_la,
        name_en: item.name_en,
        name_th: item.name_th || "",
        name_cn: item.name_cn || "",
        position: item.position,
        type: item.type || "user",
        menu_type: item.menu_type || "ALL",
        is_active: !!item.is_active,
      };
      this.headerDialog = true;
    },

    async submitHeaderForm() {
      if (this.$refs.headerForm && !this.$refs.headerForm.validate()) return;
      const body = {
        code: this.headerForm.code,
        name_la: this.headerForm.name_la,
        name_en: this.headerForm.name_en,
        name_th: this.headerForm.name_th,
        name_cn: this.headerForm.name_cn,
        position: this.headerForm.position,
        type: this.headerForm.type,
        menu_type: this.headerForm.menu_type,
        is_active: this.headerForm.is_active ? 1 : 0,
      };
      try {
        const res = this.headerEditing
          ? await this.$http.put(`menu-headers/${this.headerForm.id}`, body)
          : await this.$http.post("menu-headers", body);
        if (res.data.status) {
          this.headerDialog = false;
          await success(this, res.data.message);
          await this.fetchMenuHeaders();
          await this.fetchMenus();
        }
      } catch (e) {
        error(this, this.errMsg(e));
      }
    },

    async deleteHeader(item) {
      const result = await confirmAlert(this, this.displayName(item));
      if (!result.isConfirmed) return;
      try {
        const { data } = await this.$http.delete(`menu-headers/${item.id}`);
        if (data.status) {
          await success(this, data.message);
          await this.fetchMenuHeaders();
          await this.fetchMenus();
        }
      } catch (e) {
        error(this, this.errMsg(e));
      }
    },

    async onReorderHeaders() {
      const previousHeaders = this.menuHeaders.map((header) => ({ ...header }));
      this.menuHeaders = this.menuHeaders.map((header, index) => ({
        ...header,
        position: index + 1,
      }));

      try {
        await Promise.all(
          this.menuHeaders.map((header) =>
            this.$http.put(`menu-headers/${header.id}`, {
              code: header.code,
              name_la: header.name_la,
              name_en: header.name_en,
              name_th: header.name_th,
              name_cn: header.name_cn,
              position: header.position,
              type: header.type,
              menu_type: header.menu_type,
              is_active: header.is_active ? 1 : 0,
            })
          )
        );
        toast(this, "update_success");
        await this.fetchMenuHeaders();
      } catch (e) {
        this.menuHeaders = previousHeaders;
        error(this, this.errMsg(e));
      }
    },

    async toggleItem(item) {
      try {
        const { data } = await this.$http.patch(`toggle_menu/${item.id}`);
        if (data.status) {
          item.is_active = data.data.is_active;
          toast(this, data.message);
        }
      } catch (e) {
        error(this, this.errMsg(e));
        await this.fetchMenus(); // resync the switch on failure
      }
    },

    async deleteItem(item) {
      if (this.hasChildMenus(item)) {
        return;
      }
      const result = await confirmAlert(this, this.displayName(item));
      if (!result.isConfirmed) return;
      try {
        const { data } = await this.$http.delete(`delete_menu/${item.id}`);
        if (data.status) {
          await success(this, data.message);
          await this.fetchMenus();
        }
      } catch (e) {
        error(this, this.errMsg(e));
      }
    },

    onReorderTop() {
      const items = this.menus.map((m, i) => ({
        id: m.id,
        position: i + 1,
        parent_id: null,
      }));
      this.sendReorder(items);
    },

    onReorderChild(parent) {
      const items = (parent.all_menu || []).map((m, i) => ({
        id: m.id,
        position: i + 1,
        parent_id: parent.id,
      }));
      this.sendReorder(items);
    },

    async sendReorder(items) {
      if (!items.length) return;
      try {
        const { data } = await this.$http.post("reorder_menu", { items });
        if (data.status) toast(this, data.message);
      } catch (e) {
        error(this, this.errMsg(e));
        await this.fetchMenus();
      }
    },

    // backend returns either a translatable message key (string) or a
    // validation map { field: [messages] } — surface the first useful line
    errMsg(e) {
      const msg = e?.response?.data?.message;
      if (typeof msg === "string") return this.$te(msg) ? this.$t(msg) : msg;
      if (msg && typeof msg === "object") {
        const first = Object.values(msg)[0];
        const text = Array.isArray(first) ? first[0] : first;
        if (text) return text;
      }
      return this.$t("error");
    },
  },
};
</script>

<style>
/* LineIcons (lni lni-*) inside the menu list — plain <i> so Vuetify's v-icon
   font-family cannot override the LineIcons glyph. */
.ps-menu-lni {
  font-size: 20px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.menu-admin-card {
  border: 1px solid #e2e8f0 !important;
  border-radius: 16px !important;
  box-shadow: none !important;
  background: #ffffff !important;
  transition: border-color 0.15s ease;
  overflow: hidden;
}

.menu-admin-card:hover {
  border-color: #cbd5e1 !important;
  box-shadow: none !important;
}

.theme--dark .menu-admin-card {
  border-color: #2d3148 !important;
  background: #1e1e2d !important;
}

.theme--dark .menu-admin-card:hover {
  border-color: #3b4261 !important;
}

.menu-children-container {
  background: #f8fafc;
  border-top: 1px solid #f1f5f9;
}

.theme--dark .menu-children-container {
  background: #151824;
  border-top: 1px solid #252839;
}
</style>
